import { Pause, Play } from "lucide-react";
import { usePlayer, type PlayerTrack } from "@/lib/player/PlayerContext";

interface PlayButtonProps {
  track: PlayerTrack;
  queue?: PlayerTrack[];
  variant?: "primary" | "ghost" | "icon";
  label?: string;
  className?: string;
}

/**
 * Reusable play trigger that hands off to the global mini-player.
 * When the same track is already loaded, it toggles play/pause.
 */
export function PlayButton({ track, queue, variant = "primary", label, className }: PlayButtonProps) {
  const { current, isPlaying, playTrack, toggle } = usePlayer();
  const isCurrent = current?.id === track.id;
  const active = isCurrent && isPlaying;

  const handle = () => {
    if (isCurrent) toggle();
    else playTrack(track, queue);
  };

  const text = label ?? (active ? "Pause" : "Play");
  const Icon = active ? Pause : Play;

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={handle}
        aria-label={active ? `Pause ${track.title}` : `Play ${track.title}`}
        className={
          className ??
          "inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold-gradient text-obsidian shadow-gold hover:shadow-halo"
        }
      >
        <Icon size={16} className={active ? "" : "translate-x-[1px]"} />
      </button>
    );
  }

  if (variant === "ghost") {
    return (
      <button
        type="button"
        onClick={handle}
        className={
          className ??
          "inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs text-ivory hover:border-gold hover:text-gold"
        }
      >
        <Icon size={12} aria-hidden /> {text}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handle}
      className={
        className ??
        "inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-3 text-sm font-semibold text-obsidian shadow-gold hover:shadow-halo"
      }
    >
      <Icon size={14} /> {text}
    </button>
  );
}
